import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import AskUccPage from './pages/AskUccPage';
import DashboardPage from './pages/DashboardPage';
import BananaPage from './pages/BananaPage';
import AvailabilityPage from './pages/AvailabilityPage';
import MyFeesPage from './pages/MyFeesPage';
import MyFinesPage from './pages/MyFinesPage';
import PaymentPage from './pages/PaymentPage';
import ScorecardsPage from './pages/ScorecardsPage';
import VideosPage from './pages/VideosPage';
import AdminVideos from './pages/admin/AdminVideos';
import LeaderboardsPage from './pages/LeaderboardsPage';
import VideosPageComp from './pages/VideosPage';
import MemberStatsPage from './pages/MemberStatsPage';
import PerformancePage from './pages/PerformancePage';
import NoticePage from './pages/NoticePage';
import ProfilePage from './pages/ProfilePage';
import AttendancePage from './pages/AttendancePage';
import CheckinPage from './pages/CheckinPage';
import FloaterPage from './pages/FloaterPage';
import CharterPage from './pages/CharterPage';
import PollsPage from './pages/PollsPage';
import HeadToHeadPage from './pages/HeadToHeadPage';
import { PlayerProfilePage, RecordsPage, HeadToHeadPageV2, BananaPageV2, MatchDetailPage } from './pages/StatsV2Pages';
import PlayerStatsRedesign from './pages/PlayerStatsRedesign';
import SeriesPage from './pages/SeriesPage';
import ReimbursementsPage from './pages/ReimbursementsPage';
import OnboardingPage from './pages/OnboardingPage';
import MatchSetupPage from './pages/MatchSetup';
import MatchScoringPage from './pages/MatchScoring';
import WheelConfigPage from './pages/WheelConfigPage';

// Admin pages
import {
  AdminDashboard,
  AdminMembers,
  AdminAttendance,
  AdminFees,
  AdminFines,
  AdminAdjustments,
  AdminPenaltyAdjustments,
  AdminPayments,
  AdminMatches,
  AdminMatchControl,
  AdminScorecards,
  AdminNotices,
  AdminAvailability,
  AdminLeave,
  AdminSquadConfig,
  AdminCaptaincy,
  AdminFloaters,
  AdminSeries,
  AdminExpenses,
  AdminLunch,
  AdminAudit,
  AdminMemberReport,
  AdminRoles,
  AdminScorers,
  AdminCelebrations,
  AdminDropCatches,
  AdminSeasonImport,
  AdminTeams,
  AdminChatbot,
} from './pages/admin/AdminPages';

// Layout
import AppShell from './components/AppShell';

// Public (anonymous) — no auth wrapper. See routes/public.js for the API side.
import PublicShell from './components/PublicShell';
// Vercel Web Analytics. Must sit INSIDE <Router> — it reads the current route
// to attribute page views, and outside the router it records every visit as
// the same page. Cookie-free, so no consent banner is needed.
import { Analytics } from '@vercel/analytics/react';
import PublicMembers from './pages/public/PublicMembers';
import PrivacyPage from './pages/public/PrivacyPage';
import YouTubeLivePage from './pages/admin/YouTubeLivePage';
import BestPerformancesPage from './pages/BestPerformancesPage';
import AdminAlertsPage from './pages/admin/AdminAlertsPage';
import AdminGrievancesPage from './pages/admin/AdminGrievancesPage';
import AdminAuctionPage from './pages/admin/AdminAuctionPage';
import AuctionScreen, { AuctionControl } from './pages/AuctionLive';
import GrievancesPage from './pages/GrievancesPage';
// Public scorecards render the MEMBER component (ScorecardsPage) in
// publicMode — same year chips, match chips, tabs and tables, no duplication.

function PrivateRoute({ children }) {
  const { member, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!member) return <Navigate to="/login" replace />;
  // First-login gate: observers skip onboarding, admins skip it too
  if (!member.profile_complete && !member.is_observer && member.role === 'member') {
    return <Navigate to="/welcome" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { member, isAdmin, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!member) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

// "/" is the shop window for anyone not signed in, and the member dashboard for
// anyone who is. Deep member links (/fees, /availability…) still bounce to
// /login via PrivateRoute — only the root is public-facing.
function RootRoute({ children }) {
  const { member, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!member) return <Navigate to="/club" replace />;
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();

  // Navigation from push-notification taps. Two delivery paths, because iOS
  // standalone PWAs drop live postMessages sent while the page was hidden:
  //   1. Live: SW posts { type:'NAVIGATE', url } — handled if the page is active.
  //   2. Persisted: SW also stashes the URL in the 'ucc-pending-nav' cache; we
  //      read + clear it whenever the app becomes visible (covers the iOS case).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let lastHandled = null;
    function go(url) {
      if (!url || typeof url !== 'string') return;
      // Avoid double-handling the same target in quick succession
      if (url === lastHandled) return;
      lastHandled = url;
      navigate(url);
      // Reset the de-dupe shortly after so future taps to the same URL work
      setTimeout(() => { if (lastHandled === url) lastHandled = null; }, 1500);
    }

    const messageHandler = (event) => {
      const data = event.data || {};
      if (data.type === 'NAVIGATE') go(data.url);
    };
    navigator.serviceWorker.addEventListener('message', messageHandler);

    async function consumePendingNav() {
      try {
        if (!('caches' in window)) return;
        const cache = await caches.open('ucc-pending-nav');
        const res = await cache.match('/__pending_nav');
        if (res) {
          const url = await res.text();
          await cache.delete('/__pending_nav');
          go(url);
        }
      } catch (e) { /* ignore */ }
    }

    // Check on mount (covers cold focus) and whenever the app regains visibility.
    consumePendingNav();
    const visHandler = () => { if (document.visibilityState === 'visible') consumePendingNav(); };
    document.addEventListener('visibilitychange', visHandler);
    window.addEventListener('focus', consumePendingNav);

    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
      document.removeEventListener('visibilitychange', visHandler);
      window.removeEventListener('focus', consumePendingNav);
    };
  }, [navigate]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Super Series auction big screen — watch-only, no login (owners' tablets, projector, public) */}
      <Route path="/auction/live" element={<AuctionScreen />} />
      <Route path="/welcome" element={<OnboardingPage />} />

      {/* ── PUBLIC ROUTES — no auth wrapper, no member chrome ──
          Everything under /club is visible to anyone. Kept in its own path
          namespace so it can never collide with the member routes below
          (/records, /scorecards and /leaderboards already exist as member
          pages), and so this block is the one place to look when asking
          "what is public?" on the frontend. */}
      <Route path="/club" element={<PublicShell><DashboardPage publicMode /></PublicShell>} />
      <Route path="/privacy" element={<PublicShell><PrivacyPage /></PublicShell>} />
      <Route path="/club/members" element={<PublicShell><PublicMembers /></PublicShell>} />
      <Route path="/club/rankings" element={<PublicShell><LeaderboardsPage publicMode /></PublicShell>} />
      <Route path="/club/records" element={<PublicShell><RecordsPage publicMode /></PublicShell>} />
      <Route path="/club/honours" element={<PublicShell><BestPerformancesPage publicMode /></PublicShell>} />
      <Route path="/club/compare" element={<PublicShell><HeadToHeadPageV2 publicMode /></PublicShell>} />
      <Route path="/club/banana" element={<PublicShell><BananaPageV2 publicMode /></PublicShell>} />
      <Route path="/club/videos" element={<PublicShell><VideosPageComp publicMode /></PublicShell>} />
      <Route path="/club/ask" element={<PublicShell><AskUccPage publicMode /></PublicShell>} />
      {/* Player page. Renders the SAME component as the member route
          /stats-v2/:memberId so the public and member designs cannot
          drift. PlayerStatsRedesign takes no publicMode prop — it picks
          its API prefix from the URL via its own module-level PUB(). */}
      <Route path="/club/stats/:memberId" element={<PublicShell><PlayerStatsRedesign /></PublicShell>} />
      {/* Classic profile kept reachable (heat map, spider, pressure,
          streaks, tap-through drilldowns). Mirrors /stats-classic on the
          member side. Not linked from anywhere — delete if unwanted. */}
      <Route path="/club/stats-classic/:memberId" element={<PublicShell><PlayerProfilePage publicMode /></PublicShell>} />
      <Route path="/club/scorecards" element={<PublicShell><ScorecardsPage publicMode /></PublicShell>} />
      <Route path="/club/scorecards/:matchId" element={<PublicShell><ScorecardsPage publicMode /></PublicShell>} />

      <Route path="/" element={<RootRoute><PrivateRoute><AppShell><DashboardPage /></AppShell></PrivateRoute></RootRoute>} />
      <Route path="/ask" element={<PrivateRoute><AppShell><AskUccPage /></AppShell></PrivateRoute>} />
      <Route path="/availability" element={<PrivateRoute><AppShell><AvailabilityPage /></AppShell></PrivateRoute>} />
      <Route path="/fees" element={<PrivateRoute><AppShell><MyFeesPage /></AppShell></PrivateRoute>} />
      <Route path="/fines" element={<PrivateRoute><AppShell><MyFinesPage /></AppShell></PrivateRoute>} />
      <Route path="/payment" element={<PrivateRoute><AppShell><PaymentPage /></AppShell></PrivateRoute>} />
      <Route path="/scorecards" element={<PrivateRoute><AppShell><ScorecardsPage /></AppShell></PrivateRoute>} />
      <Route path="/scorecards/:matchId" element={<PrivateRoute><AppShell><ScorecardsPage /></AppShell></PrivateRoute>} />
      <Route path="/videos" element={<PrivateRoute><AppShell><VideosPage /></AppShell></PrivateRoute>} />
      <Route path="/leaderboards" element={<PrivateRoute><AppShell><LeaderboardsPage /></AppShell></PrivateRoute>} />
      <Route path="/banana" element={<PrivateRoute><AppShell><BananaPage /></AppShell></PrivateRoute>} />
      <Route path="/stats/:memberId" element={<PrivateRoute><AppShell><MemberStatsPage /></AppShell></PrivateRoute>} />
      <Route path="/performance" element={<PrivateRoute><AppShell><PerformancePage /></AppShell></PrivateRoute>} />
      <Route path="/notices" element={<PrivateRoute><AppShell><NoticePage /></AppShell></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><AppShell><ProfilePage /></AppShell></PrivateRoute>} />
      <Route path="/attendance" element={<PrivateRoute><AppShell><AttendancePage /></AppShell></PrivateRoute>} />
      <Route path="/checkin" element={<PrivateRoute><AppShell><CheckinPage /></AppShell></PrivateRoute>} />
      <Route path="/floaters" element={<PrivateRoute><AppShell><FloaterPage /></AppShell></PrivateRoute>} />
      <Route path="/charter" element={<PrivateRoute><AppShell><CharterPage /></AppShell></PrivateRoute>} />
      <Route path="/polls" element={<PrivateRoute><AppShell><PollsPage /></AppShell></PrivateRoute>} />
      <Route path="/compare" element={<PrivateRoute><AppShell><HeadToHeadPage /></AppShell></PrivateRoute>} />
      <Route path="/stats-v2/:memberId" element={<PrivateRoute><AppShell><PlayerStatsRedesign /></AppShell></PrivateRoute>} />
      <Route path="/stats-classic/:memberId" element={<PrivateRoute><AppShell><PlayerProfilePage /></AppShell></PrivateRoute>} />
      <Route path="/stats-v3/:memberId" element={<PrivateRoute><AppShell><PlayerStatsRedesign /></AppShell></PrivateRoute>} />
      <Route path="/records" element={<PrivateRoute><AppShell><RecordsPage /></AppShell></PrivateRoute>} />
      <Route path="/compare-v2" element={<PrivateRoute><AppShell><HeadToHeadPageV2 /></AppShell></PrivateRoute>} />
      <Route path="/banana-v2" element={<PrivateRoute><AppShell><BananaPageV2 /></AppShell></PrivateRoute>} />
      <Route path="/match/:matchId" element={<PrivateRoute><AppShell><MatchDetailPage /></AppShell></PrivateRoute>} />
      <Route path="/series" element={<PrivateRoute><AppShell><SeriesPage /></AppShell></PrivateRoute>} />
      <Route path="/best-performances" element={<PrivateRoute><AppShell><BestPerformancesPage /></AppShell></PrivateRoute>} />
      <Route path="/reimbursements" element={<PrivateRoute><AppShell><ReimbursementsPage /></AppShell></PrivateRoute>} />
      <Route path="/grievances" element={<PrivateRoute><AppShell><GrievancesPage /></AppShell></PrivateRoute>} />
      <Route path="/matches/:matchId/setup" element={<PrivateRoute><AppShell><MatchSetupPage /></AppShell></PrivateRoute>} />
      <Route path="/matches/:matchId/score" element={<PrivateRoute><AppShell><MatchScoringPage /></AppShell></PrivateRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AppShell><AdminDashboard /></AppShell></AdminRoute>} />
      <Route path="/admin/members" element={<AdminRoute><AppShell><AdminMembers /></AppShell></AdminRoute>} />
      <Route path="/admin/attendance" element={<AdminRoute><AppShell><AdminAttendance /></AppShell></AdminRoute>} />
      <Route path="/admin/fees" element={<AdminRoute><AppShell><AdminFees /></AppShell></AdminRoute>} />
      <Route path="/admin/fines" element={<AdminRoute><AppShell><AdminFines /></AppShell></AdminRoute>} />
      <Route path="/admin/adjustments" element={<AdminRoute><AppShell><AdminAdjustments /></AppShell></AdminRoute>} />
      <Route path="/admin/youtube" element={<AdminRoute><AppShell><YouTubeLivePage /></AppShell></AdminRoute>} />
      <Route path="/admin/penalty-adjustments" element={<AdminRoute><AppShell><AdminPenaltyAdjustments /></AppShell></AdminRoute>} />
      <Route path="/admin/payments" element={<AdminRoute><AppShell><AdminPayments /></AppShell></AdminRoute>} />
      <Route path="/admin/matches" element={<AdminRoute><AppShell><AdminMatches /></AppShell></AdminRoute>} />
      <Route path="/admin/match-control" element={<AdminRoute><AppShell><AdminMatchControl /></AppShell></AdminRoute>} />
      <Route path="/admin/scorecards" element={<AdminRoute><AppShell><AdminScorecards /></AppShell></AdminRoute>} />
      <Route path="/admin/videos" element={<AdminRoute><AppShell><AdminVideos /></AppShell></AdminRoute>} />
      <Route path="/admin/notices" element={<AdminRoute><AppShell><AdminNotices /></AppShell></AdminRoute>} />
      <Route path="/admin/availability" element={<AdminRoute><AppShell><AdminAvailability /></AppShell></AdminRoute>} />
      <Route path="/admin/leave" element={<AdminRoute><AppShell><AdminLeave /></AppShell></AdminRoute>} />
      <Route path="/admin/squad-config" element={<AdminRoute><AppShell><AdminSquadConfig /></AppShell></AdminRoute>} />
      <Route path="/admin/captaincy" element={<AdminRoute><AppShell><AdminCaptaincy /></AppShell></AdminRoute>} />
      <Route path="/admin/floaters" element={<AdminRoute><AppShell><AdminFloaters /></AppShell></AdminRoute>} />
      <Route path="/admin/series" element={<AdminRoute><AppShell><AdminSeries /></AppShell></AdminRoute>} />
      <Route path="/admin/expenses" element={<AdminRoute><AppShell><AdminExpenses /></AppShell></AdminRoute>} />
      <Route path="/admin/lunch" element={<AdminRoute><AppShell><AdminLunch /></AppShell></AdminRoute>} />
      <Route path="/admin/member-report" element={<AdminRoute><AppShell><AdminMemberReport /></AppShell></AdminRoute>} />
      <Route path="/admin/roles" element={<AdminRoute><AppShell><AdminRoles /></AppShell></AdminRoute>} />
      <Route path="/admin/alerts" element={<AdminRoute><AppShell><AdminAlertsPage /></AppShell></AdminRoute>} />
      <Route path="/admin/alerts/:id" element={<AdminRoute><AppShell><AdminAlertsPage /></AppShell></AdminRoute>} />
      <Route path="/admin/grievances" element={<AdminRoute><AppShell><AdminGrievancesPage /></AppShell></AdminRoute>} />
      <Route path="/admin/auction" element={<AdminRoute><AppShell><AdminAuctionPage /></AppShell></AdminRoute>} />
      <Route path="/admin/auction/live/:id" element={<AdminRoute><AppShell><AuctionControl /></AppShell></AdminRoute>} />
      <Route path="/admin/grievances/:id" element={<AdminRoute><AppShell><AdminGrievancesPage /></AppShell></AdminRoute>} />
      <Route path="/admin/scorers" element={<AdminRoute><AppShell><AdminScorers /></AppShell></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute><AppShell><AdminAudit /></AppShell></AdminRoute>} />
      <Route path="/admin/chatbot" element={<AdminRoute><AppShell><AdminChatbot /></AppShell></AdminRoute>} />
      <Route path="/admin/celebrations" element={<AdminRoute><AppShell><AdminCelebrations /></AppShell></AdminRoute>} />
      <Route path="/admin/drop-catches" element={<AdminRoute><AppShell><AdminDropCatches /></AppShell></AdminRoute>} />
      <Route path="/admin/season-import" element={<AdminRoute><AppShell><AdminSeasonImport /></AppShell></AdminRoute>} />
      <Route path="/admin/teams" element={<AdminRoute><AppShell><AdminTeams /></AppShell></AdminRoute>} />
      <Route path="/admin/wheel-setup" element={<AdminRoute><AppShell><WheelConfigPage /></AppShell></AdminRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // ── Auto cache-bust: detect new deploy and hard-reload ──
  React.useEffect(() => {
    const VERSION_KEY = 'ucc_build_version';
    fetch('/api/version', { cache: 'no-store' })
      .then(r => r.json())
      .then(({ version }) => {
        const stored = localStorage.getItem(VERSION_KEY);
        if (stored && stored !== version) {
          localStorage.setItem(VERSION_KEY, version);
          window.location.reload(true);
        } else {
          localStorage.setItem(VERSION_KEY, version);
        }
      })
      .catch(() => {}); // silent fail — don't break app if offline
  }, []);

  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <Analytics />
        <Toaster
          position="top-center"
          gutter={8}
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: "'Barlow', sans-serif",
              fontWeight: 500,
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '0.9rem',
              maxWidth: '360px',
            },
            success: { style: { background: '#d8f3dc', color: '#2d6a4f' }, iconTheme: { primary: '#2d6a4f', secondary: '#fff' } },
            error: { style: { background: '#fde8e8', color: '#c62828' }, iconTheme: { primary: '#c62828', secondary: '#fff' } },
          }}
        />
      </Router>
    </AuthProvider>
  );
}
